import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import toast from 'react-hot-toast';
import { GET_TXIMPORT_PREVIEW } from '../graphql/queries/tximport';
import { SET_TXIMPORT_JOB_CANCELED, SET_TXIMPORT_PREVIEW_SKIPPED, SET_TXIMPORT_PREVIEW_STOCK, SET_TXIMPORT_PREVIEW_TOKEN, START_TXIMPORT, UPDATE_TXIMPORT_PREVIEW_TRANSACTION, UPDATE_TXIMPORT_MAPPING } from '../graphql/mutations/tximport';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorAlert from '../components/ErrorAlert';
import RecordsTable from '../components/imports-review/RecordsTable';
import ConfirmModal from '../components/ConfirmModal';
import EditPreviewModal from '../components/imports-review/EditPreviewModal';
import SetTokenModal from '../components/imports-review/SetTokenModal';
import SetStockModal from '../components/imports-review/SetStockModal';
import EditMappingModal from '../components/imports-review/EditMappingModal';
import { usePortfolioContext } from '../contexts/PortfolioContext';
import { IMPORTS_RESULT_ROUTE_PREFIX, IMPORTS_REVIEW_ROUTE_PREFIX, IMPORTS_ROUTE, STOCK_IMPORTS_RESULT_ROUTE_PREFIX, STOCK_IMPORTS_REVIEW_ROUTE_PREFIX, STOCK_IMPORTS_ROUTE } from '../constants/routes';

const PAGE_SIZE = 100;
const FILTER_MODES = {
  ALL: 'all',
  VALID: 'valid',
  INVALID: 'invalid',
};

const productName = (record) => {
  try {
    const original = JSON.parse(record?.originalRow || 'null');
    const idx = (original?.headers || []).findIndex((h) => /^(product|produkt)$/i.test((h || '').trim()));
    return idx >= 0 ? original.values?.[idx] || '' : '';
  } catch {
    return '';
  }
};

const ImportsReviewPage = ({ assetKind = 'crypto' }) => {
  const routeIsStock = assetKind === 'stock';
  const importsRoute = routeIsStock ? STOCK_IMPORTS_ROUTE : IMPORTS_ROUTE;
  const resultRoutePrefix = routeIsStock ? STOCK_IMPORTS_RESULT_ROUTE_PREFIX : IMPORTS_RESULT_ROUTE_PREFIX;
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { currentPortfolio } = usePortfolioContext();
  const [page, setPage] = useState(0);
  const [filterMode, setFilterMode] = useState(FILTER_MODES.ALL);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [overallTotal, setOverallTotal] = useState(0);

  const onlyInvalid = filterMode === FILTER_MODES.INVALID;
  const queryVars = useMemo(
    () => ({ jobId, limit: PAGE_SIZE, offset: page * PAGE_SIZE, onlyInvalid }),
    [jobId, page, onlyInvalid],
  );

  const { data, loading, error, refetch } = useQuery(GET_TXIMPORT_PREVIEW, {
    variables: queryVars,
    skip: !jobId,
    notifyOnNetworkStatusChange: true,
  });
  const [setSkipped] = useMutation(SET_TXIMPORT_PREVIEW_SKIPPED);
  const [cancelJob, { loading: cancelJobLoading }] = useMutation(SET_TXIMPORT_JOB_CANCELED);
  const [updatePreviewTransaction, { loading: updateTransactionLoading }] = useMutation(UPDATE_TXIMPORT_PREVIEW_TRANSACTION);
  const [startImport, { loading: startImportLoading }] = useMutation(START_TXIMPORT);
  const [setPreviewToken, { loading: setTokenLoading }] = useMutation(SET_TXIMPORT_PREVIEW_TOKEN);
  const [updateMapping, { loading: updateMappingLoading }] = useMutation(UPDATE_TXIMPORT_MAPPING);
  const [setPreviewStock, { loading: setStockLoading }] = useMutation(SET_TXIMPORT_PREVIEW_STOCK);
  const [showSetStockModal, setShowSetStockModal] = useState(false);
  const [pendingRowId, setPendingRowId] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showGroupActions, setShowGroupActions] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showSetTokenModal, setShowSetTokenModal] = useState(false);
  const [showEditMappingModal, setShowEditMappingModal] = useState(false);
  const groupActionsRef = useRef(null);

  const previewData = data?.tximportPreview;
  const total = previewData?.total ?? 0;
  const readyCount = previewData?.readyCount ?? 0;
  const records = useMemo(() => previewData?.items ?? [], [previewData]);
  const mappingPlan = previewData?.mappingPlan;
  const job = previewData?.job;
  const isStock = job?.assetKind === 'stock';
  const unmatchedCount = isStock ? records.filter((r) => r.rawIsin && !r.stockId).length : 0;
  const firstSelectedRecord = useMemo(
    () => records.find((r) => selectedIds.has(r.id)),
    [records, selectedIds],
  );

  // Redirect if job belongs to a different portfolio or route namespace
  useEffect(() => {
    if (job?.portfolioId && currentPortfolio?.id && job.portfolioId !== currentPortfolio.id) {
      toast.error('This job belongs to a different portfolio');
      navigate(importsRoute);
      return;
    }
    if (job?.assetKind) {
      const jobIsStock = job.assetKind === 'stock';
      if (jobIsStock !== routeIsStock) {
        const prefix = jobIsStock ? STOCK_IMPORTS_REVIEW_ROUTE_PREFIX : IMPORTS_REVIEW_ROUTE_PREFIX;
        navigate(`${prefix}/${jobId}`, { replace: true });
      }
    }
  }, [job?.portfolioId, job?.assetKind, currentPortfolio?.id, navigate, importsRoute, routeIsStock, jobId]);

  // When filtering valid only, show readyCount; otherwise 'total' reflects the filtered count
  const viewTotalRows = filterMode === FILTER_MODES.VALID ? readyCount : (total ?? 0);
  const jobTotalRows = overallTotal > 0 ? overallTotal : viewTotalRows;
  // Backend filters when onlyInvalid is set; client-side filter needed only for valid-only mode
  const filteredRecords = useMemo(
    () => (filterMode === FILTER_MODES.VALID ? records.filter((r) => r.isValid) : records),
    [filterMode, records],
  );

  const fieldMappings = useMemo(() => {
    if (!mappingPlan?.fields?.length) return null;
    return mappingPlan.fields.reduce((acc, field) => {
      if (field?.targetField) {
        acc[field.targetField.toUpperCase()] = field;
      }
      return acc;
    }, {});
  }, [mappingPlan]);

  // Get original row data from first record for mapping preview
  const firstOriginalRow = useMemo(() => {
    if (!records.length) return null;
    try {
      const parsed = typeof records[0].originalRow === 'string'
        ? JSON.parse(records[0].originalRow)
        : records[0].originalRow;
      return parsed;
    } catch {
      return null;
    }
  }, [records]);
  const totalPages = Math.max(1, Math.ceil(viewTotalRows / PAGE_SIZE));
  
  // Job must be WAITING + REVIEWING to allow edits
  const canEdit = job?.status === 'WAITING' && job?.stage === 'REVIEWING';
  const canCancelJob = job?.status === 'WAITING';

  const importCtaLabel = useMemo(() => {
    if (readyCount <= 0) {
      return 'Import records';
    }
    if (jobTotalRows > 0 && readyCount === jobTotalRows) {
      const noun = readyCount === 1 ? 'record' : 'records';
      return `Import ${readyCount.toLocaleString()} ${noun}`;
    }
    const totalLabel = jobTotalRows > 0 ? jobTotalRows.toLocaleString() : '0';
    return `Import ${readyCount.toLocaleString()}/${totalLabel} records`;
  }, [jobTotalRows, readyCount]);

  const handleStartImport = async () => {
    if (!jobId) return;
    try {
      await startImport({ variables: { jobId } });
      toast.success('Import started');
      navigate(`${resultRoutePrefix}/${jobId}`);
    } catch (err) {
      console.error('Failed to start import', err);
      toast.error('Failed to start import. Please try again.');
    }
  };

  useEffect(() => {
    if (!onlyInvalid && total > 0) {
      setOverallTotal(total);
    }
  }, [onlyInvalid, total]);

  useEffect(() => {
    setOverallTotal(0);
  }, [jobId]);

  const handlePrevPage = () => {
    setPage((prev) => Math.max(0, prev - 1));
    setSelectedIds(new Set());
  };

  const handleNextPage = () => {
    setPage((prev) => (prev + 1 < totalPages ? prev + 1 : prev));
    setSelectedIds(new Set());
  };

  const handleFilterChange = (mode) => {
    setFilterMode(mode);
    setPage(0);
    setSelectedIds(new Set());
  };

  const handleSkipChange = async (rowId, skipped) => {
    setPendingRowId(rowId);
    try {
      await setSkipped({ variables: { rowId, skipped } });
      await refetch(queryVars);
    } catch (err) {
      console.error('Failed to update skip state', err);
    } finally {
      setPendingRowId(null);
    }
  };

  const handleConfirmCancel = async () => {
    try {
      await cancelJob({ variables: { jobId } });
      toast.success('Import job canceled');
      setShowCancelModal(false);
      navigate(importsRoute);
    } catch (err) {
      console.error('Failed to cancel job', err);
      toast.error('Failed to cancel job. Please try again.');
    }
  };

  const handleOpenEditModal = (record) => {
    setEditingRecord(record);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setEditingRecord(null);
    setShowEditModal(false);
  };

  const handleSaveEdit = async (payload) => {
    if (!editingRecord) return;
    try {
      await updatePreviewTransaction({
        variables: { rowId: editingRecord.id, input: payload },
      });
      toast.success('Preview transaction updated');
      setEditingRecord(null);
      setShowEditModal(false);
      await refetch(queryVars);
    } catch (err) {
      console.error('Failed to update preview transaction', err);
      toast.error('Failed to update preview transaction. Please try again.');
    }
  };

  const handleBulkExclude = async () => {
    if (selectedIds.size === 0) return;
    setBulkActionLoading(true);
    setShowGroupActions(false);
    try {
      const ids = Array.from(selectedIds);
      await Promise.all(ids.map((rowId) => setSkipped({ variables: { rowId, skipped: true } })));
      toast.success(`Excluded ${ids.length} record${ids.length === 1 ? '' : 's'}`);
      setSelectedIds(new Set());
      await refetch(queryVars);
    } catch (err) {
      console.error('Failed to exclude records', err);
      toast.error('Failed to exclude some records. Please try again.');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkInclude = async () => {
    if (selectedIds.size === 0) return;
    setBulkActionLoading(true);
    setShowGroupActions(false);
    try {
      const ids = Array.from(selectedIds);
      await Promise.all(ids.map((rowId) => setSkipped({ variables: { rowId, skipped: false } })));
      toast.success(`Included ${ids.length} record${ids.length === 1 ? '' : 's'}`);
      setSelectedIds(new Set());
      await refetch(queryVars);
    } catch (err) {
      console.error('Failed to include records', err);
      toast.error('Failed to include some records. Please try again.');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleSetToken = () => {
    setShowGroupActions(false);
    setShowSetTokenModal(true);
  };

  const handleSaveSetToken = async ({ tokenId, coingeckoApiId }) => {
    if (selectedIds.size === 0 || (!tokenId && !coingeckoApiId)) return;
    try {
      const rowIds = Array.from(selectedIds);
      await setPreviewToken({ variables: { rowIds, tokenId, coingeckoApiId } });
      toast.success(`Token set for ${rowIds.length} record${rowIds.length === 1 ? '' : 's'}`);
      setSelectedIds(new Set());
      setShowSetTokenModal(false);
      await refetch(queryVars);
    } catch (err) {
      console.error('Failed to set token', err);
      toast.error('Failed to set token. Please try again.');
    }
  };

  const handleSaveSetStock = async ({ stockId, symbol }) => {
    if (selectedIds.size === 0 || (!stockId && !symbol)) return;
    try {
      const rowIds = Array.from(selectedIds);
      const { data: result } = await setPreviewStock({ variables: { rowIds, stockId, symbol } });
      const updated = result?.setTximportPreviewStock?.length ?? 0;
      if (updated === 0) {
        toast.error('No rows were updated. Only stock rows of an import awaiting review can be changed.');
      } else {
        toast.success(`Stock set for ${updated} record${updated === 1 ? '' : 's'}`);
      }
      setSelectedIds(new Set());
      setShowSetStockModal(false);
      await refetch(queryVars);
    } catch (err) {
      console.error('Failed to set stock', err);
      toast.error('Failed to set stock. Please try again.');
    }
  };

  const handleSaveMapping = async ({ fields }) => {
    if (!jobId || !fields?.length) return;
    try {
      await updateMapping({
        variables: {
          jobId,
          fields: fields.map((f) => ({
            targetField: f.targetField,
            strategyKind: f.strategy.kind,
            column: f.strategy.column,
            columnIndex: f.strategy.columnIndex,
            columns: f.strategy.columns,
            columnIndices: f.strategy.columnIndices,
            separator: f.strategy.separator,
          })),
        },
      });
      toast.success('Mapping updated. Re-processing file...');
      setShowEditMappingModal(false);
      // Navigate to imports list - job will be re-queued
      navigate(importsRoute);
    } catch (err) {
      console.error('Failed to update mapping', err);
      toast.error('Failed to update mapping. Please try again.');
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showGroupActions) return undefined;
    const handleClickOutside = (event) => {
      if (groupActionsRef.current && !groupActionsRef.current.contains(event.target)) {
        setShowGroupActions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showGroupActions]);

  if (!jobId) {
    return (
      <div className="px-2 sm:px-3 lg:px-4 py-2 space-y-6">
        <ErrorAlert message="Missing job identifier" />
      </div>
    );
  }

  return (
    <div className="px-2 sm:px-3 lg:px-4 py-2 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{isStock ? 'Review Stock Import' : 'Review Import'}</h1>
          <Link to={importsRoute} className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
            ← Back to Tasks
          </Link>
        </div>
        <div className="flex flex-wrap gap-3 flex-shrink-0">
            {canEdit && !isStock && (
              <button
                type="button"
                onClick={() => setShowEditMappingModal(true)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none"
              >
                Edit Mapping
              </button>
            )}
            <button
              type="button"
              disabled={!canEdit || readyCount <= 0 || startImportLoading}
              onClick={handleStartImport}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {startImportLoading ? 'Starting…' : importCtaLabel}
            </button>
            {canCancelJob && (
              <button
                onClick={() => setShowCancelModal(true)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none"
              >
                Cancel Job
              </button>
            )}
          </div>
      </div>
      <div>
        <p className="text-gray-600 dark:text-gray-400">Job ID: <span className="font-mono text-sm break-all">{jobId}</span></p>
        <p className="text-gray-600 dark:text-gray-400">Total rows: {jobTotalRows.toLocaleString()}</p>
        {job && (
          <p className="text-gray-600 dark:text-gray-400">
            Status:{' '}
            <span className="font-semibold">{job.status}</span>
            {job.stage ? ` • Stage: ${job.stage}` : ''}
          </p>
        )}
      </div>

      {loading && !records.length ? (
        <div className="py-12">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <ErrorAlert message="Failed to load preview rows. Please try again." />
      ) : (
        <>
          {!canEdit && job && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3 text-sm text-yellow-800 dark:text-yellow-200">
              <strong>Read-only:</strong> This job is no longer editable (status: {job.status}, stage: {job.stage}).
              {job.status === 'SUCCEEDED' && ' Import completed successfully.'}
              {job.status === 'FAILED' && ' Import failed.'}
              {job.status === 'RUNNING' && ' Import is in progress.'}
            </div>
          )}
          {canEdit && unmatchedCount > 0 && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3 text-sm text-yellow-800 dark:text-yellow-200">
              {unmatchedCount} {unmatchedCount === 1 ? 'row' : 'rows'} on this page could not be matched to a stock by ISIN.
              Select {unmatchedCount === 1 ? 'it' : 'them'} and choose <strong>Group actions → Set stock</strong>, or skip them.
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600 dark:text-gray-400 mb-3">
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative" ref={groupActionsRef}>
                <button
                  type="button"
                  disabled={!canEdit || selectedIds.size === 0 || bulkActionLoading}
                  onClick={() => setShowGroupActions((prev) => !prev)}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {bulkActionLoading ? 'Processing…' : `Group actions${selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}`}
                  <svg className="ml-2 -mr-0.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showGroupActions && (
                  <div className="absolute left-0 mt-1 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-10">
                    <div className="py-1" role="menu">
                      <button
                        type="button"
                        onClick={handleBulkExclude}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        role="menuitem"
                      >
                        Exclude from import
                      </button>
                      <button
                        type="button"
                        onClick={handleBulkInclude}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        role="menuitem"
                      >
                        Include in import
                      </button>
                      {isStock ? (
                        <button
                          type="button"
                          onClick={() => {
                            setShowGroupActions(false);
                            setShowSetStockModal(true);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                          role="menuitem"
                        >
                          Set stock
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={handleSetToken}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                          role="menuitem"
                        >
                          Set token
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <span>
                Showing{' '}
                {viewTotalRows === 0
                  ? '0'
                  : `${(page * PAGE_SIZE + 1).toLocaleString()}-${Math.min(
                      (page + 1) * PAGE_SIZE,
                      viewTotalRows,
                    ).toLocaleString()}`}
                {' '}of {viewTotalRows.toLocaleString()} rows
              </span>
              <div className="inline-flex rounded-md overflow-hidden border border-gray-300 dark:border-gray-600" role="group">
                <button
                  type="button"
                  onClick={() => handleFilterChange(FILTER_MODES.ALL)}
                  className={`px-3 py-1 text-sm font-medium ${filterMode === FILTER_MODES.ALL ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'}`}
                >
                  Show all
                </button>
                <button
                  type="button"
                  onClick={() => handleFilterChange(FILTER_MODES.VALID)}
                  className={`px-3 py-1 text-sm font-medium border-l border-gray-300 dark:border-gray-600 ${filterMode === FILTER_MODES.VALID ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'}`}
                >
                  Only valid
                </button>
                <button
                  type="button"
                  onClick={() => handleFilterChange(FILTER_MODES.INVALID)}
                  className={`px-3 py-1 text-sm font-medium border-l border-gray-300 dark:border-gray-600 ${filterMode === FILTER_MODES.INVALID ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'}`}
                >
                  Only invalid
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrevPage}
                disabled={page === 0 || loading}
                className="px-3 py-1 rounded-md border border-gray-300 dark:border-gray-600 text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <span>
                Page {Math.min(page + 1, totalPages)} of {totalPages}
              </span>
              <button
                onClick={handleNextPage}
                disabled={page + 1 >= totalPages || loading}
                className="px-3 py-1 rounded-md border border-gray-300 dark:border-gray-600 text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
          <RecordsTable
            records={filteredRecords}
            expandedRowId={expandedRowId}
            onToggleRow={(id) => setExpandedRowId(id)}
            onToggleSkip={canEdit ? handleSkipChange : undefined}
            pendingRowId={pendingRowId}
            fieldMappings={fieldMappings}
            onEditRecord={canEdit ? handleOpenEditModal : undefined}
            selectedIds={selectedIds}
            onSelectionChange={canEdit ? setSelectedIds : undefined}
            readOnly={!canEdit}
            assetKind={isStock ? 'stock' : 'crypto'}
          />
        </>
      )}
      <ConfirmModal
        isOpen={showCancelModal}
        title="Cancel import job?"
        message="This will stop the current import and discard any pending changes. Continue?"
        confirmLabel="Yes, cancel job"
        onConfirm={handleConfirmCancel}
        onCancel={() => setShowCancelModal(false)}
        loading={cancelJobLoading}
      />
      <EditPreviewModal
        isOpen={showEditModal}
        record={editingRecord}
        isStock={isStock}
        onClose={handleCloseEditModal}
        onSave={handleSaveEdit}
        loading={updateTransactionLoading}
      />
      <SetTokenModal
        isOpen={showSetTokenModal}
        selectedCount={selectedIds.size}
        onClose={() => setShowSetTokenModal(false)}
        onSave={handleSaveSetToken}
        loading={setTokenLoading}
      />
      <SetStockModal
        isOpen={showSetStockModal}
        selectedCount={selectedIds.size}
        initialQuery={productName(firstSelectedRecord)}
        hint={firstSelectedRecord?.rawIsin
          ? `Stock row: ${[firstSelectedRecord.rawIsin, firstSelectedRecord.rawExchange, firstSelectedRecord.currency].filter(Boolean).join(' · ')}. Pick the listing that trades in the row's currency.`
          : ''}
        onClose={() => setShowSetStockModal(false)}
        onSave={handleSaveSetStock}
        loading={setStockLoading}
      />
      <EditMappingModal
        isOpen={showEditMappingModal}
        onClose={() => setShowEditMappingModal(false)}
        onSave={handleSaveMapping}
        loading={updateMappingLoading}
        mappingPlan={mappingPlan}
        originalRow={firstOriginalRow}
      />
    </div>
  );
};

export default ImportsReviewPage;

